import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiLoader, FiSave } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import SearchableSelect from '../../components/common/SearchableSelect';

const emptyForm = {
  name: '',
  contact_person: '',
  phone: '',
  email: '',
  fax: '',
  address: '',
  gst_no: '',
  ntn_no: '',
  cnic: '',
  account_id: '',
  account_mode: 'none',
  is_active: true,
};

export default function SupplierForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    api.accounts.list({ per_page: 1000 }).then(({ data }) => {
      const list = Array.isArray(data.data) ? data.data : data.accounts || data;
      setAccounts(Array.isArray(list) ? list : list.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.suppliers.get(id).then(({ data }) => {
      const s = data.supplier || data;
      setForm({
        name: s.name || '',
        contact_person: s.contact_person || '',
        phone: s.phone || '',
        email: s.email || '',
        fax: s.fax || '',
        address: s.address || '',
        gst_no: s.gst_no || '',
        ntn_no: s.ntn_no || '',
        cnic: s.cnic || '',
        account_id: s.account_id || '',
        account_mode: s.account_id ? 'link' : 'none',
        is_active: s.is_active !== false,
      });
    }).catch((err) => showError('Failed to load supplier', extractError(err))).finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    const errs = {};
    if (!form.name) errs.name = 'Supplier name is required';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        contact_person: form.contact_person,
        phone: form.phone,
        email: form.email,
        fax: form.fax,
        address: form.address,
        gst_no: form.gst_no,
        ntn_no: form.ntn_no,
        cnic: form.cnic,
        account_mode: form.account_mode,
        account_id: form.account_mode === 'link' && form.account_id ? Number(form.account_id) : undefined,
        is_active: form.is_active,
      };

      if (isEdit) {
        await api.suppliers.update(id, payload);
        toast('success', '', 'Supplier updated successfully');
      } else {
        await api.suppliers.create(payload);
        toast('success', '', 'Supplier created successfully');
      }
      navigate('/suppliers');
    } catch (err) {
      showError('Failed to save supplier', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page"><div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div></div>;

  return (
    <div className="page">
      <PageHeader
        title={isEdit ? 'Edit Supplier' : 'New Supplier'}
        subtitle={isEdit ? `Editing ${form.name}` : 'Create a new supplier record. Account linking is optional — by default no account is created.'}
        crumbs={[{ label: 'Finance', to: '/suppliers' }, { label: 'Suppliers', to: '/suppliers' }, { label: isEdit ? 'Edit' : 'New Supplier' }]}
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => navigate('/suppliers')}><FiArrowLeft /> Back</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><FiLoader /> Saving...</> : <><FiSave /> {isEdit ? 'Update' : 'Create'} Supplier</>}
            </button>
          </>
        }
      />

      <div className="card">
        <div className="card-header"><div className="card-title">Basic Information</div></div>
        <div className="card-body">
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Supplier Name <span className="req">*</span></label>
              <input className={`form-control ${errors.name ? 'is-invalid' : ''}`} value={form.name} onChange={(e) => { set('name', e.target.value); if (errors.name) setErrors((p) => ({ ...p, name: '' })); }} placeholder="Company or individual name" />
              {errors.name && <div className="invalid-feedback">{errors.name}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Contact Person</label>
              <input className="form-control" value={form.contact_person} onChange={(e) => set('contact_person', e.target.value)} placeholder="Contact person name" />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'end', paddingBottom: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
                <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
                Active supplier
              </label>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Account</label>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 4 }}>
              {[['none', 'No account'], ['create', 'Create new account'], ['link', 'Link existing account']].map(([mode, label]) => (
                <label key={mode} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  <input type="radio" name="account_mode" value={mode} checked={form.account_mode === mode} onChange={() => set('account_mode', mode)} />
                  {label}
                </label>
              ))}
            </div>
            {form.account_mode === 'link' ? (
              <div style={{ marginTop: 8 }}>
                <SearchableSelect
                  options={accounts}
                  value={form.account_id}
                  onChange={(v) => set('account_id', v)}
                  placeholder="Select existing account to link"
                  getLabel={(a) => a.name}
                />
              </div>
            ) : (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                {form.account_mode === 'create'
                  ? 'A new account will be created from Settings when you save.'
                  : 'Supplier will be saved without any ledger account (default).'}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header"><div className="card-title">Contact Details</div></div>
        <div className="card-body">
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-control" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="Phone number" />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-control" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="email@company.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Fax</label>
              <input className="form-control" value={form.fax} onChange={(e) => set('fax', e.target.value)} placeholder="Fax number" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <input className="form-control" value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="Full address" />
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-header"><div className="card-title">Tax & Registration</div></div>
        <div className="card-body">
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">GST No.</label>
              <input className="form-control" value={form.gst_no} onChange={(e) => set('gst_no', e.target.value)} placeholder="GST registration number" />
            </div>
            <div className="form-group">
              <label className="form-label">NTN No.</label>
              <input className="form-control" value={form.ntn_no} onChange={(e) => set('ntn_no', e.target.value)} placeholder="NTN registration number" />
            </div>
            <div className="form-group">
              <label className="form-label">CNIC</label>
              <input className="form-control" value={form.cnic} onChange={(e) => set('cnic', e.target.value)} placeholder="e.g. 42101-1234567-8" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
